// Reviews & Feedback: every review, rating and piece of feedback across both
// apps, newest first, read directly from the app databases.
// Backed by GET /secureApi/analytics/user-activity/reviews (users:access).

import React, { useEffect, useState } from "react";
import axios from "axios";
import { Section, StateBanner, Tile, fmtInt } from "../../components/bigquery/bqShared.jsx";
import { ReviewsTable } from "./userActivity.jsx";

const SOURCES = [
  { key: "all", label: "All" },
  { key: "legacy_feedback", label: "App feedback (legacy)" },
  { key: "legacy_rating", label: "Ratings (legacy)" },
  { key: "ai_recipe", label: "Recipe reviews (AI app)" },
  { key: "ai_barbot", label: "Barbot feedback (AI app)" },
];
const PAGE_SIZE = 50;

export default function ReviewsFeedback() {
  const [source, setSource] = useState("all");
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ data: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    axios
      .get(`${process.env.REACT_APP_ADMIN_API_ADDRESS}/secureApi/analytics/user-activity/reviews`, {
        params: { source, page, pageSize: PAGE_SIZE },
        headers: { Authorization: `Bearer ${localStorage.getItem("jwtToken")}` },
      })
      .then((r) => !cancelled && setState({ data: r.data.data, loading: false, error: null }))
      .catch((e) => !cancelled && setState({ data: null, loading: false, error: e.response?.status === 403 ? "You need the Users permission." : e.response?.data?.message || e.message }));
    return () => {
      cancelled = true;
    };
  }, [source, page]);

  const d = state.data;
  const t = d?.totals || {};
  const pages = d ? Math.max(1, Math.ceil(d.total / PAGE_SIZE)) : 1;

  return (
    <div>
      <ul className="breadcrumb">
        <li className="breadcrumb-item">USERS</li>
        <li className="breadcrumb-item active">REVIEWS &amp; FEEDBACK</li>
      </ul>
      <h1 className="page-header">
        Reviews &amp; Feedback <small>both apps, read directly from the app databases</small>
      </h1>
      <StateBanner loading={state.loading} error={state.error} hasData={!!d} />

      {d && (
        <div style={{ opacity: state.loading ? 0.5 : 1 }}>
          {d.unavailable?.length > 0 && (
            <div className="alert alert-warning py-2 small">
              The dashboard API's database login cannot read the AI app review tables yet ({d.unavailable.join(", ")}). Legacy reviews are shown; AI app reviews will appear once read access is granted.
            </div>
          )}
          <div className="row">
            <Tile label="App feedback (legacy)" value={fmtInt(t.legacy_feedback)} sub="written feedback" />
            <Tile label="Ratings (legacy)" value={fmtInt(t.legacy_rating)} />
            <Tile label="Recipe reviews (AI app)" value={t.ai_recipe === null ? "no access" : fmtInt(t.ai_recipe)} sub={t.ai_recipe === null ? "API cannot read this table" : t.ai_recipe === 0 ? "feature not used yet" : null} />
            <Tile label="Barbot feedback (AI app)" value={t.ai_barbot === null ? "no access" : fmtInt(t.ai_barbot)} sub={t.ai_barbot === null ? "API cannot read this table" : t.ai_barbot === 0 ? "feature not used yet" : null} />
          </div>
          <Section
            title="ALL REVIEWS & FEEDBACK"
            subtitle={`${fmtInt(d.total)} entries, newest first`}
            right={
              <select className="form-select form-select-sm w-auto" value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }}>
                {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            }
            bodyClass="p-0"
          >
            <ReviewsTable rows={d.rows} showUser />
            <div className="d-flex align-items-center small px-3 py-2">
              <span className="text-inverse text-opacity-50">Click a user to see everything they crafted.</span>
              <div className="ms-auto btn-group">
                <button className="btn btn-sm btn-outline-theme" disabled={page <= 1 || state.loading} onClick={() => setPage((p) => p - 1)}>‹</button>
                <span className="btn btn-sm btn-outline-theme disabled">{page} / {pages}</span>
                <button className="btn btn-sm btn-outline-theme" disabled={page >= pages || state.loading} onClick={() => setPage((p) => p + 1)}>›</button>
              </div>
            </div>
          </Section>
        </div>
      )}
    </div>
  );
}
